import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { setProgress } from "../../redux/Slice/playerSlice";
import {
  FooterPlayerProgressAudio,
  FooterPlayerThumb,
  FooterPlayerAudioRange,
  FooterPlayerLoadedAudio,
} from "./styled";
import { HiddenPlayer } from "../HiddenPlayer/HiddenPlayer";
import { formatProgressTime } from "../../utils/formatProgressTime";
import { useFloating, autoUpdate, offset, shift } from "@floating-ui/react";
import { playerInfoSelector } from "../../redux/selectors/playerSelector";
import { useMediaSession } from "../../hooks/useMediaSession";

export const FooterPlayerRange = () => {
  const dispatch = useDispatch();
  const {
    duration,
    progress,
    loaded,
    audioType,
    currentStreamType,
    currentContent,
  } = useSelector(playerInfoSelector);
  const [isDragging, setIsDragging] = useState(false);
  const [seekTime, setSeekTime] = useState(0);
  const [tooltipContent, setTooltipContent] = useState("");
  const audioRef = useRef(null);
  const playerRef = useRef(null);
  const loadedRef = useRef(null);

  useMediaSession(playerRef);

  const { x, y, refs, strategy } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: "top",
    middleware: [offset(10), shift()],
  });

  // Вычисление времени для seek на основе позиции клика
  const calculateSeekTime = useCallback(
    (clientX) => {
      const audioRect = audioRef.current.getBoundingClientRect();
      const clickPosition = clientX - audioRect.left;
      const audioWidth = audioRect.width;
      return (clickPosition / audioWidth) * duration;
    },
    [duration],
  );

  // Обработчик для завершения перетаскивания
  const handleDragEnd = useCallback(
    (e) => {
      if (!isDragging) return;

      setIsDragging(false);
      const clientX = e.changedTouches
        ? e.changedTouches[0].clientX
        : e.clientX;
      const time = calculateSeekTime(clientX);
      setSeekTime(time);
      dispatch(setProgress(time));
      playerRef.current.seekTo(time, "seconds");
    },
    [isDragging, calculateSeekTime, dispatch],
  );

  // Обработчик для изменения прогресса при перетаскивании
  const handleSeek = useCallback(
    (e) => {
      if (isDragging) {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const time = calculateSeekTime(clientX);
        setSeekTime(time);
      }
    },
    [isDragging, calculateSeekTime],
  );

  // Обработчик для клика по полосе прогресса
  const handleClick = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const time = calculateSeekTime(clientX);
    setSeekTime(time);
    dispatch(setProgress(time));
    playerRef.current.seekTo(time, "seconds");
  };

  // Обработчик для отображения тултипа при наведении
  const handleMouseMove = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const time = calculateSeekTime(clientX);
    setTooltipContent(formatProgressTime(time));

    // Получаем координаты полосы прокрутки
    const audioRect = audioRef.current.getBoundingClientRect();

    // Устанавливаем фиксированную высоту тултипа (15px над полосой)
    const tooltipY = audioRect.top - 15;
    const tooltipX = clientX; // Следуем за курсором по горизонтали

    refs.setReference({
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        x: tooltipX,
        y: tooltipY,
        top: tooltipY,
        right: tooltipX,
        bottom: tooltipY,
        left: tooltipX,
      }),
    });
  };

  useEffect(() => {
    const handleMouseUp = (e) => handleDragEnd(e);
    const handleTouchEnd = (e) => handleDragEnd(e);

    if (isDragging) {
      window.addEventListener("mousemove", handleSeek);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleSeek);
      window.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleSeek);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleSeek);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, handleSeek, handleDragEnd]);

  useEffect(() => {
    if (progress && !isDragging) {
      setSeekTime(progress);
    }
  }, [progress, isDragging]);

  const progressPercentage =
    ((isDragging ? seekTime : progress) / duration) * 100;
  const loadedPercentage = (loaded / duration) * 100;

  useLayoutEffect(() => {
    const loadedLine = loadedRef.current;
    if (!loadedLine) return;

    loadedLine.style.transition = "none";

    loadedLine.style.width = "0";

    // Принудительный reflow
    void loadedLine.offsetWidth;

    loadedLine.style.transition = "width 2s ease-in-out";
  }, [currentContent.id]);

  return (
    <>
      <FooterPlayerAudioRange
        ref={audioRef}
        onClick={audioType !== "stream" ? handleClick : undefined}
        onMouseDown={() => audioType !== "stream" && setIsDragging(true)}
        onMouseMove={audioType !== "stream" ? handleMouseMove : undefined}
        onMouseLeave={() => audioType !== "stream" && setTooltipContent("")}
        onTouchStart={() => audioType !== "stream" && setIsDragging(true)}
        onTouchMove={audioType !== "stream" ? handleMouseMove : undefined}
        onTouchEnd={() => audioType !== "stream" && setTooltipContent("")}
        isStream={audioType === "stream"}
      >
        <div key={currentContent.id}>
          <FooterPlayerLoadedAudio
            ref={loadedRef}
            style={{
              width: `${loadedPercentage}%`,
            }}
          />
        </div>
        <FooterPlayerProgressAudio
          typeStream={currentStreamType}
          style={{
            width: `${audioType === "stream" ? 100 : progressPercentage}%`,
          }}
        />
        {audioType !== "stream" && (
          <FooterPlayerThumb
            style={{
              left: `${progressPercentage}%`,
            }}
          />
        )}
      </FooterPlayerAudioRange>
      {tooltipContent && audioType !== "stream" && (
        <div
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            color: "#fff",
            textShadow: "0px 0px 10px rgba(0,0,0,1)",
          }}
        >
          {tooltipContent}
        </div>
      )}

      {audioType !== "stream" && <HiddenPlayer ref={playerRef} />}
    </>
  );
};
